import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc,
  setDoc,
  updateDoc,
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  Timestamp,
  runTransaction,
  deleteDoc
} from 'firebase/firestore';
import { SystemSettingsService } from './systemSettingsService';

const QUEUE_COLLECTION = 'task_queue';
const WORKFLOW_EXECUTIONS = 'workflow_executions';
const PUSH_HISTORY = 'pushHistory';
const TEMPLATE_EXECUTIONS = 'template_executions';

// Simple in-memory queue for API call management
let apiQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_API_CALLS = 5;
let activeApiCalls = 0;

export const QueueManagerService = {
  // Add a task to the in-memory API queue
  addToQueue: (task) => {
    return new Promise((resolve, reject) => {
      console.log(`[QueueManager] Adding task to queue, current length: ${apiQueue.length}, active calls: ${activeApiCalls}/${MAX_CONCURRENT_API_CALLS}`);
      
      // Add the task to the queue with its resolve/reject callbacks
      apiQueue.push({
        task,
        resolve,
        reject
      });
      
      // Start processing the queue if not already processing
      if (!isProcessingQueue && activeApiCalls < MAX_CONCURRENT_API_CALLS) {
        console.log(`[QueueManager] Starting queue processing immediately`);
        setTimeout(() => {
          QueueManagerService.processApiQueue();
        }, 0);
      } else {
        console.log(`[QueueManager] Queue processing already in progress or at capacity. Task queued for later processing.`);
      }
    });
  },
  
  // Process the in-memory API queue
  processApiQueue: async () => {
    if (isProcessingQueue) return;
    
    isProcessingQueue = true;
    console.log(`[QueueManager] Processing API queue with ${apiQueue.length} items, active calls: ${activeApiCalls}/${MAX_CONCURRENT_API_CALLS}`);
    
    try {
      // Process as many items as we can up to the concurrent limit
      while (apiQueue.length > 0 && activeApiCalls < MAX_CONCURRENT_API_CALLS) {
        const { task, resolve, reject } = apiQueue.shift();
        activeApiCalls++;
        
        console.log(`[QueueManager] Starting task, active calls: ${activeApiCalls}/${MAX_CONCURRENT_API_CALLS}, remaining in queue: ${apiQueue.length}`);
        
        // Execute the task asynchronously
        (async () => {
          try {
            const result = await task();
            resolve(result);
            console.log(`[QueueManager] Task completed successfully`);
          } catch (error) {
            console.error(`[QueueManager] Task failed with error:`, error);
            reject(error);
          } finally {
            activeApiCalls--;
            console.log(`[QueueManager] Task finished, active calls: ${activeApiCalls}/${MAX_CONCURRENT_API_CALLS}, remaining in queue: ${apiQueue.length}`);
            
            // Check if we need to process more items
            if (apiQueue.length > 0 && !isProcessingQueue) {
              console.log(`[QueueManager] Restarting queue processing for remaining ${apiQueue.length} items`);
              QueueManagerService.processApiQueue();
            }
          }
        })();
      }
    } catch (error) {
      console.error(`[QueueManager] Error in queue processing:`, error);
    } finally {
      isProcessingQueue = false;
      
      // If there are still items in the queue and we have capacity, continue processing
      if (apiQueue.length > 0 && activeApiCalls < MAX_CONCURRENT_API_CALLS) {
        console.log(`[QueueManager] Continuing to process queue with ${apiQueue.length} remaining items`);
        setTimeout(() => {
          QueueManagerService.processApiQueue();
        }, 0);
      } else {
        console.log(`[QueueManager] Queue processing paused. Active calls: ${activeApiCalls}/${MAX_CONCURRENT_API_CALLS}, Items in queue: ${apiQueue.length}`);
      }
    }
  },

  // Enqueue a new task
  enqueueTask: async (taskData) => {
    const settings = await SystemSettingsService.getSettings();
    
    if (!settings.taskQueueEnabled) {
      throw new Error('Task queue is currently disabled');
    }

    // Get current queue size
    const queuedQuery = query(
      collection(db, QUEUE_COLLECTION),
      where('status', '==', 'queued')
    );
    const queueSnapshot = await getDocs(queuedQuery);
    
    if (queueSnapshot.size >= settings.maxQueueSize) {
      throw new Error('Queue is full');
    }

    const taskRef = doc(collection(db, QUEUE_COLLECTION));
    const task = {
      id: taskRef.id,
      ...taskData,
      status: 'queued',
      createdAt: Timestamp.now(),
      attempts: 0,
      priority: taskData.priority || 1,
      type: taskData.type || 'generic',
      timeout: Timestamp.fromDate(
        new Date(Date.now() + (settings.taskTimeoutMinutes * 60 * 1000))
      )
    };

    await setDoc(taskRef, task);
    return task;
  },

  // Process next task in queue
  processNextTask: async () => {
    const settings = await SystemSettingsService.getSettings();
    
    if (!settings.taskQueueEnabled) {
      return null;
    }

    // Check running tasks
    const runningQuery = query(
      collection(db, QUEUE_COLLECTION),
      where('status', '==', 'running')
    );
    const runningSnapshot = await getDocs(runningQuery);
    
    if (runningSnapshot.size >= settings.maxConcurrentTasks) {
      return null;
    }

    // Get next task considering priority, creation time, and any delay for retries
    const now = Timestamp.now();
    const nextTaskQuery = query(
      collection(db, QUEUE_COLLECTION),
      where('status', '==', 'queued'),
      where('nextAttemptTime', '<=', now),
      orderBy('nextAttemptTime'),
      orderBy('priority', 'desc'),
      orderBy('createdAt', 'asc'),
      limit(1)
    );

    const snapshot = await getDocs(nextTaskQuery);
    if (snapshot.empty) {
      return null;
    }

    const taskDoc = snapshot.docs[0];
    const task = { id: taskDoc.id, ...taskDoc.data() };

    // Start task
    await updateDoc(doc(db, QUEUE_COLLECTION, task.id), {
      status: 'running',
      startTime: Timestamp.now(),
      attempts: task.attempts + 1,
      timeout: Timestamp.fromDate(
        new Date(Date.now() + (settings.taskTimeoutMinutes * 60 * 1000))
      )
    });

    return task;
  },

  // Handle task completion
  completeTask: async (taskId, result) => {
    await runTransaction(db, async (transaction) => {
      const taskRef = doc(db, QUEUE_COLLECTION, taskId);
      const taskDoc = await transaction.get(taskRef);
      
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const task = taskDoc.data();
      
      // Update task status
      transaction.update(taskRef, {
        status: 'completed',
        completedTime: Timestamp.now(),
        result
      });

      // Update related collections based on task type
      if (task.type === 'workflow') {
        const workflowRef = doc(db, WORKFLOW_EXECUTIONS, task.workflowId);
        transaction.update(workflowRef, {
          lastCompletedStep: task.stepNumber,
          [`steps.${task.stepNumber}.status`]: 'completed',
          [`steps.${task.stepNumber}.result`]: result,
          [`steps.${task.stepNumber}.completedAt`]: Timestamp.now()
        });
      } else if (task.type === 'shopify_push') {
        const pushRef = doc(collection(db, PUSH_HISTORY));
        transaction.set(pushRef, {
          taskId,
          userId: task.userId,
          status: 'success',
          result,
          timestamp: Timestamp.now()
        });
      } else if (task.type === 'template') {
        const executionRef = doc(collection(db, TEMPLATE_EXECUTIONS));
        transaction.set(executionRef, {
          taskId,
          templateId: task.templateId,
          userId: task.userId,
          status: 'completed',
          result,
          completedAt: Timestamp.now()
        });
      }
    });

    // Try to process next task
    await QueueManagerService.processNextTask();
  },

  // Handle task failure
  handleTaskFailure: async (taskId, error) => {
    const settings = await SystemSettingsService.getSettings();
    const taskRef = doc(db, QUEUE_COLLECTION, taskId);
    
    await runTransaction(db, async (transaction) => {
      const taskDoc = await transaction.get(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const task = taskDoc.data();
      
      if (task.attempts >= settings.retryAttempts) {
        // Mark as failed if max retries reached
        transaction.update(taskRef, {
          status: 'failed',
          failedTime: Timestamp.now(),
          error: error.message || 'Unknown error'
        });

        // Update related collections
        if (task.type === 'workflow') {
          const workflowRef = doc(db, WORKFLOW_EXECUTIONS, task.workflowId);
          transaction.update(workflowRef, {
            status: 'failed',
            error: error.message || 'Unknown error',
            endTime: Timestamp.now(),
            [`steps.${task.stepNumber}.status`]: 'failed',
            [`steps.${task.stepNumber}.error`]: error.message || 'Unknown error',
            [`steps.${task.stepNumber}.failedAt`]: Timestamp.now()
          });
        } else if (task.type === 'shopify_push') {
          const pushRef = doc(collection(db, PUSH_HISTORY));
          transaction.set(pushRef, {
            taskId,
            userId: task.userId,
            status: 'failed',
            error: error.message || 'Unknown error',
            timestamp: Timestamp.now()
          });
        } else if (task.type === 'template') {
          const executionRef = doc(collection(db, TEMPLATE_EXECUTIONS));
          transaction.set(executionRef, {
            taskId,
            templateId: task.templateId,
            userId: task.userId,
            status: 'failed',
            error: error.message || 'Unknown error',
            failedAt: Timestamp.now()
          });
        }
      } else {
        // Requeue with delay
        const nextAttemptTime = new Date();
        nextAttemptTime.setSeconds(nextAttemptTime.getSeconds() + settings.retryDelaySeconds);
        
        transaction.update(taskRef, {
          status: 'queued',
          nextAttemptTime: Timestamp.fromDate(nextAttemptTime),
          error: error.message || 'Unknown error'
        });
      }
    });

    // Try to process next task
    await QueueManagerService.processNextTask();
  },

  // Get task status
  getTaskStatus: async (taskId) => {
    const taskDoc = await getDoc(doc(db, QUEUE_COLLECTION, taskId));
    if (!taskDoc.exists()) {
      throw new Error('Task not found');
    }
    return taskDoc.data();
  },

  // Get queue statistics
  getQueueStats: async () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const [runningSnapshot, queuedSnapshot, completedSnapshot, failedSnapshot] = await Promise.all([
      getDocs(query(collection(db, QUEUE_COLLECTION), where('status', '==', 'running'))),
      getDocs(query(collection(db, QUEUE_COLLECTION), where('status', '==', 'queued'))),
      getDocs(query(
        collection(db, QUEUE_COLLECTION), 
        where('status', '==', 'completed'),
        where('completedTime', '>=', Timestamp.fromDate(yesterday))
      )),
      getDocs(query(
        collection(db, QUEUE_COLLECTION),
        where('status', '==', 'failed'),
        where('failedTime', '>=', Timestamp.fromDate(yesterday))
      ))
    ]);

    return {
      running: runningSnapshot.size,
      queued: queuedSnapshot.size,
      completed24h: completedSnapshot.size,
      failed24h: failedSnapshot.size,
      timestamp: Timestamp.now()
    };
  },

  // Clean up old completed/failed tasks
  cleanupOldTasks: async (daysToKeep = 7) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const oldTasksQuery = query(
      collection(db, QUEUE_COLLECTION),
      where('status', 'in', ['completed', 'failed']),
      where('completedTime', '<=', cutoffTimestamp)
    );

    const snapshot = await getDocs(oldTasksQuery);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    return snapshot.size;
  },

  // Cancel a running or queued task
  cancelTask: async (taskId) => {
    const taskRef = doc(db, QUEUE_COLLECTION, taskId);
    
    await runTransaction(db, async (transaction) => {
      const taskDoc = await transaction.get(taskRef);
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      const task = taskDoc.data();
      if (task.status !== 'running' && task.status !== 'queued') {
        throw new Error('Can only cancel running or queued tasks');
      }

      transaction.update(taskRef, {
        status: 'cancelled',
        cancelledTime: Timestamp.now(),
        error: 'Task cancelled by user'
      });

      // Update related collections
      if (task.type === 'workflow') {
        const workflowRef = doc(db, WORKFLOW_EXECUTIONS, task.workflowId);
        transaction.update(workflowRef, {
          status: 'cancelled',
          error: 'Workflow cancelled by user',
          endTime: Timestamp.now()
        });
      } else if (task.type === 'template') {
        const executionRef = doc(collection(db, TEMPLATE_EXECUTIONS));
        transaction.set(executionRef, {
          taskId,
          templateId: task.templateId,
          userId: task.userId,
          status: 'cancelled',
          error: 'Template execution cancelled by user',
          cancelledAt: Timestamp.now()
        });
      }
    });
  }
};
